import React, { useState } from 'react';
import { Edit3, Lock } from 'lucide-react';
import { formatDate } from '../contracts/contractDetailUtils';
import StructuredArrayTable from './StructuredArrayTable';
import StructuredFieldEditor from './StructuredFieldEditor';

// Shared typed renderer for structured-data outputs — used by both the
// contract tab's cards and the admin editor's test-run pane, so the dry run
// previews exactly what the tab will show. One row per SCHEMA field in schema
// order; fields with no value render an em dash (absence is information).
// Phase b: pass provenance + canEdit + onSaveField to get per-field
// edit-in-place (hover Edit3 icon) and lock markers on human-edited fields.

function ScalarChipList({ items }) {
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}>
      {items.map((item, i) => (
        <span key={i} className="chip neutral">{String(item)}</span>
      ))}
    </span>
  );
}

function ObjectPairs({ value, size = 12 }) {
  const entries = Object.entries(value || {});
  if (entries.length === 0) return <NullValue />;
  return (
    <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'minmax(90px, max-content) 1fr', gap: '4px 18px', fontSize: size }}>
      {entries.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className="tertiary" style={{ fontSize: size }}>{k}</dt>
          <dd style={{ margin: 0 }}>{typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function NullValue() {
  return (
    <span style={{ color: 'var(--text-tertiary)' }} title="Not found in the document">—</span>
  );
}

export function StructuredFieldValue({ field, value, canEdit = false, onSaveValue = null, rowHandlers = null }) {
  if (value === null || value === undefined || value === '') return <NullValue />;
  switch (field.type) {
    case 'number':
      return <span className="mono">{typeof value === 'number' ? value.toLocaleString() : String(value)}</span>;
    case 'date':
      return <span className="mono">{formatDate(value)}</span>;
    case 'boolean':
      return value
        ? <span>Yes</span>
        : <span style={{ color: 'var(--text-secondary)' }}>No</span>;
    case 'enum':
      return <span className="chip neutral">{String(value)}</span>;
    case 'array': {
      const items = Array.isArray(value) ? value : [value];
      if (items.length === 0) return <NullValue />;
      const allScalar = items.every((v) => typeof v !== 'object' || v === null);
      if (allScalar) return <ScalarChipList items={items} />;
      const allObjects = items.every((v) => typeof v === 'object' && v !== null && !Array.isArray(v));
      if (allObjects) {
        return (
          <StructuredArrayTable
            items={items}
            itemFields={field.itemFields}
            canEdit={canEdit}
            onSave={onSaveValue}
            onRowAdd={rowHandlers?.onRowAdd || null}
            onRowUpdate={rowHandlers?.onRowUpdate || null}
            onRowDelete={rowHandlers?.onRowDelete || null}
          />
        );
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {items.map((item, i) => (
            <div key={i} style={{ padding: '6px 0', borderTop: i === 0 ? 0 : '0.5px solid var(--border-primary)' }}>
              {typeof item === 'object' && item !== null
                ? <ObjectPairs value={item} />
                : String(item)}
            </div>
          ))}
        </div>
      );
    }
    case 'object':
      return <ObjectPairs value={value} />;
    default: {
      const asStr = String(value);
      const isLong = asStr.length > 40;
      return (
        <span style={{ fontStyle: isLong ? 'italic' : 'normal', whiteSpace: 'pre-wrap' }}>
          {isLong ? `"${asStr}"` : asStr}
        </span>
      );
    }
  }
}

// The <dl> grid — exactly the clauses idiom on the contract detail page.
export default function StructuredValues({
  fields = [],
  values = {},
  provenance = {},
  canEdit = false,
  onSaveField = null,
  // Row-level array ops (doc-46): { onRowAdd, onRowUpdate, onRowDelete },
  // each taking (fieldKey, ...) — wired only on the contract tab.
  rowOps = null,
  renderRowExtras,
}) {
  const [editingKey, setEditingKey] = useState(null);
  return (
    <dl style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(160px, max-content) 1fr',
      gap: '14px 40px',
      margin: 0,
      fontSize: 13,
      lineHeight: 1.6,
    }}>
      {fields.map((field) => {
        const prov = provenance?.[field.key];
        const isHuman = prov?.source === 'human';
        const editing = canEdit && editingKey === field.key;
        return (
          <React.Fragment key={field.key}>
            <dt className="tertiary" style={{ fontSize: 12, paddingTop: 1 }}>{field.label || field.key}</dt>
            <dd className="sd-row" style={{ margin: 0, color: 'var(--text-primary)', minWidth: 0 }}>
              {editing ? (
                <StructuredFieldEditor
                  field={field}
                  value={values?.[field.key]}
                  onCancel={() => setEditingKey(null)}
                  onSave={async (v) => {
                    await onSaveField(field.key, v);
                    setEditingKey(null);
                  }}
                />
              ) : (
                <span style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <StructuredFieldValue
                      field={field}
                      value={values?.[field.key]}
                      canEdit={canEdit}
                      onSaveValue={onSaveField ? (v) => onSaveField(field.key, v) : null}
                      rowHandlers={rowOps && field.type === 'array' ? {
                        onRowAdd: rowOps.onRowAdd ? (row) => rowOps.onRowAdd(field.key, row) : null,
                        onRowUpdate: rowOps.onRowUpdate ? (index, row) => rowOps.onRowUpdate(field.key, index, row) : null,
                        onRowDelete: rowOps.onRowDelete ? (index) => rowOps.onRowDelete(field.key, index) : null,
                      } : null}
                    />
                    {isHuman && (
                      <span
                        role="img"
                        title={`Edited by ${prov.editedByName || 'unknown'}, ${formatDate(prov.editedAt)} — kept on rerun`}
                        aria-label={`Edited by ${prov.editedByName || 'unknown'}, ${formatDate(prov.editedAt)} — kept on rerun`}
                        style={{ display: 'inline-flex', marginLeft: 6, verticalAlign: '-1px' }}
                      >
                        <Lock className="w-3 h-3" style={{ color: 'var(--text-tertiary)' }} />
                      </span>
                    )}
                  </span>
                  {canEdit && (
                    <button
                      type="button"
                      className="icon-btn xs sd-row-edit"
                      title="Edit"
                      aria-label={`Edit ${field.label || field.key}`}
                      onClick={() => setEditingKey(field.key)}
                    >
                      <Edit3 className="w-3 h-3" />
                    </button>
                  )}
                </span>
              )}
              {renderRowExtras ? renderRowExtras(field) : null}
            </dd>
          </React.Fragment>
        );
      })}
    </dl>
  );
}
